import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GraphView from './graphView';
import NodeDetailsPanel from './NodeDetailsPanel';
import GraphSidePanel from './GraphSidePanel';
import FeatureTeamFilter from './FeatureTeamFilter';  // 👈 NEW: Import FeatureTeamFilter
import VideoTutorialModal from './VideoTutorialModal'; // 👈 NEW: Import VideoTutorialModal
import './GraphSection.css';
import SelectedNodeCartouche from './SelectedNodeCartouche';

const GraphSection = ({
  nodes,
  allRelationships,
  selectedNode,
  setSelectedNode,
  setGraphRefreshFn,
  loading,
  generateTests,
  setTestScenarios,
  setLoading,
  API_BASE_URL,
  onRefreshData, // Nouvelle prop pour rafraîchir les données globales
  openLabSection, // Nouvelle prop pour ouvrir les sections lab
  user, // Ajout de user pour le tenantId
  onOpenTestGeneration, // Nouvelle prop pour ouvrir la modal de génération de tests
  onFiltersReady // 👈 NEW: Callback pour exposer les fonctions de filtre
}) => {
  const { t } = useTranslation();
  // États pour gérer le side panel
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelType, setSidePanelType] = useState(null);
  const [sidePanelData, setSidePanelData] = useState(null);
  const [graphRefreshFunction, setGraphRefreshFunction] = useState(null);

  // État pour le modal tutoriel vidéo 👈 NEW
  const [showGraphTutorial, setShowGraphTutorial] = useState(false);

  // États pour le filtre par équipes 👈 NEW
  const [colorMode, setColorMode] = useState('type');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [showLegend, setShowLegend] = useState(true);
  const [filterCollapsed, setFilterCollapsed] = useState(() => {
    // 👈 NEW: Persist collapse state in localStorage
    const saved = localStorage.getItem('featureFilterCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // 👈 NEW: Persist collapse state
  useEffect(() => {
    localStorage.setItem('featureFilterCollapsed', JSON.stringify(filterCollapsed));
  }, [filterCollapsed]);

  // 👈 NEW: Fonction pour nettoyer les filtres d'équipes inexistantes
  const cleanupTeamFilters = () => {
    const existingTeams = [...new Set(nodes.map(n => n.featureTeam).filter(Boolean))];
    const validSelectedTeams = selectedTeams.filter(team => existingTeams.includes(team));
    
    // Si des équipes n'existent plus, mettre à jour la sélection
    if (validSelectedTeams.length !== selectedTeams.length) {
      console.log('🧹 Nettoyage des filtres: équipes supprimées détectées');
      setSelectedTeams(validSelectedTeams);
      
      // Si plus aucune équipe sélectionnée, afficher tout
      if (validSelectedTeams.length === 0) {
        console.log('📊 Aucune équipe restante - affichage de tous les nœuds');
      }
    }
  };

  // 👈 NEW: Nettoyer les filtres quand les nœuds changent
  useEffect(() => {
    cleanupTeamFilters();
  }, [nodes]);

  // 👈 NEW: Exposer les fonctions de contrôle des filtres au parent
  useEffect(() => {
    if (onFiltersReady) {
      onFiltersReady({
        cleanupTeamFilters,
        clearAllFilters: () => setSelectedTeams([]),
        resetColorMode: () => setColorMode('type')
      });
    }
  }, [onFiltersReady]);

  // Fonction pour ouvrir le side panel
  const openSidePanel = (type, data = null) => {
    setSidePanelType(type);
    setSidePanelData(data);
    setSidePanelOpen(true);
  };

  // Fonction pour fermer le side panel
  const closeSidePanel = () => {
    setSidePanelOpen(false);
    setSidePanelType(null);
    setSidePanelData(null);
  };

  // Fonction appelée après sauvegarde/suppression
  const handleSidePanelAction = () => {
    // Appeler le refresh du graphe si disponible
    if (graphRefreshFunction) {
      graphRefreshFunction();
    }
    // Déclencher un refresh global des données si nécessaire
    if (onRefreshData) {
      onRefreshData();
    }
    closeSidePanel();
  };

  return (
    <div id="section-graph" className="graph-section">
      {/* Floating cartouche — always below the section icon, anchored to graph-section */}
      <SelectedNodeCartouche selectedNode={selectedNode} onClose={() => setSelectedNode(null)} />
      <div className="graph-section-header">
        <div className="graph-section-icon">
          <span className="graph-section-icon-emoji">🕸️</span>
        </div>
        <div className="graph-section-content">
          <h3 className="graph-section-title">
            {t('graphSection.title')}
          </h3>
          <p className="graph-section-desc">
            {t('graphSection.description')}
          </p>
        </div>
      </div>
   <div className={`feature-team-filter-panel-horizontal ${filterCollapsed ? 'collapsed' : ''}`}>
          <button 
            className={`filter-toggle-btn-horizontal ${filterCollapsed ? 'collapsed' : ''}`}
            onClick={() => setFilterCollapsed(!filterCollapsed)}
            title={filterCollapsed ? t('graphSection.expandFilter', 'Expand filter') : t('graphSection.collapseFilter', 'Collapse filter')}
            aria-expanded={!filterCollapsed}
            aria-controls="feature-team-filter-content"
          >
            <span className="filter-icon" aria-hidden>👥</span>
            <span className="filter-text">{filterCollapsed ? t('graphSection.show', 'Filters') : t('graphSection.hide', 'Hide')}</span>
          </button>
          
          <div id="feature-team-filter-content" className={`filter-content-horizontal ${filterCollapsed ? 'collapsed' : ''}`}>
            {console.log('GraphSection rendering FeatureTeamFilter:', { showLegend, filterCollapsed })}
            <FeatureTeamFilter
              nodes={nodes}
              colorMode={colorMode}
              onColorModeChange={setColorMode}
              selectedTeams={selectedTeams}
              onTeamSelectionChange={setSelectedTeams}
              showLegend={showLegend}
              onShowLegendChange={setShowLegend}
              compact={true}
            />
          </div>
        </div>
      <div className={`graph-details-layout ${filterCollapsed ? 'filter-collapsed' : ''}`}>
        {/* Panneau Filtre par équipes 👈 NEW */}

        {/* Panneau Graphe */}
        <div className="graph-panel">
          <GraphView
            key={nodes.map(n => n.name).join(",") + allRelationships.length}
            nodes={nodes}
            relationships={allRelationships}
            onNodeSelect={setSelectedNode}
            selectedNode={selectedNode}
            colorMode={colorMode}              // 👈 NEW: Pass color mode
            selectedTeams={selectedTeams}      // 👈 NEW: Pass selected teams
            showLegend={showLegend}            // 👈 NEW: Pass legend visibility
            legendInFilter={true}
            onGraphRefresh={fn => {
              setGraphRefreshFunction(() => fn);
              setGraphRefreshFn(() => fn);
            }}
            onOpenSidePanel={openSidePanel}
            onOpenTutorialModal={() => setShowGraphTutorial(true)} // 👈 NEW: Pass tutorial modal handler
          />
        </div>

        {/* Panneau Détails */}
        <div className="details-panel">
          <NodeDetailsPanel
            selectedNode={selectedNode}
            loading={loading}
            generateTests={generateTests}
            setTestScenarios={setTestScenarios}
            setLoading={setLoading}
            API_BASE_URL={API_BASE_URL}
            openLabSection={openLabSection}
            user={user}
            fetchNodes={onRefreshData}
            onOpenTestGeneration={onOpenTestGeneration}
          />
        </div>
      </div>

      {/* Side Panel moderne */}
      <GraphSidePanel
        isOpen={sidePanelOpen}
        onClose={closeSidePanel}
        type={sidePanelType}
        data={sidePanelData}
        onSave={handleSidePanelAction}
        onDelete={handleSidePanelAction}
        availableNodes={nodes}
      />

      {/* Modal Tutoriel Vidéo Graph View 👈 NEW */}
      <VideoTutorialModal
        isOpen={showGraphTutorial}
        onClose={() => setShowGraphTutorial(false)}
        video={{
          id: 'graphTutorial',
          title: 'Tutorial - Graph View',
          description: 'Learn how to use and explore the complete visualization of your knowledge graph. Create, Update, Delete nodes and relationships, and utilize advanced features to maximize your graph experience. The next generation of living documentation is here!',
          duration: '1:34',
          videoId: 'ePE3nEqdme8',
          thumbnail: 'https://img.youtube.com/vi/ePE3nEqdme8/hqdefault.jpg'
        }}
        allVideos={[]}
      />
    </div>
  );
};

export default GraphSection;
